/**
 * Marketing Attribution & UTM Tracking Layer
 * Captures, persists, and extracts campaign tracking parameters across visitor journeys.
 */

export interface AttributionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  [key: string]: string | null;
}

const STORAGE_KEY = 'iiab_utm';
const COOKIE_NAME = 'iiab_utm';
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
}

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax${
    isSecure ? '; Secure' : ''
  }`;
}

/**
 * Parses UTM parameters from window.location.search on app load
 * and persists to sessionStorage & 30-day first-party cookie.
 */
export function initAttribution(): AttributionData | null {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');
    const term = params.get('utm_term');
    const content = params.get('utm_content');

    // If any UTM parameter is detected in the current query string
    if (source || medium || campaign || term || content) {
      const attribution: AttributionData = {
        utm_source: source ? source.trim() : null,
        utm_medium: medium ? medium.trim() : null,
        utm_campaign: campaign ? campaign.trim() : null,
        utm_term: term ? term.trim() : null,
        utm_content: content ? content.trim() : null,
      };

      const serialized = JSON.stringify(attribution);

      // Persist into sessionStorage
      try {
        sessionStorage.setItem(STORAGE_KEY, serialized);
      } catch {
        // Ignore storage quota or disabled storage
      }

      // Persist into 30-day first-party cookie
      try {
        setCookie(COOKIE_NAME, serialized, COOKIE_MAX_AGE_SECONDS);
      } catch {
        // Ignore cookie write errors
      }

      return attribution;
    }
  } catch (err) {
    console.warn('[Attribution] Failed to parse UTM parameters from URL:', err);
  }

  return null;
}

/**
 * Returns currently stored attribution data, checking URL query, sessionStorage, and cookie.
 */
export function getAttributionData(): Record<string, string | null> {
  const defaultEmpty: AttributionData = {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
  };

  if (typeof window === 'undefined') {
    return defaultEmpty;
  }

  // 1. Check if current URL contains UTM parameters
  const currentUrlAttribution = initAttribution();
  if (currentUrlAttribution) {
    return currentUrlAttribution;
  }

  // 2. Check sessionStorage
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') {
        return {
          utm_source: parsed.utm_source || null,
          utm_medium: parsed.utm_medium || null,
          utm_campaign: parsed.utm_campaign || null,
          utm_term: parsed.utm_term || null,
          utm_content: parsed.utm_content || null,
        };
      }
    }
  } catch {
    // Fall through to cookie
  }

  // 3. Check 30-day first-party cookie
  try {
    const cookieVal = getCookie(COOKIE_NAME);
    if (cookieVal) {
      const parsed = JSON.parse(cookieVal);
      if (parsed && typeof parsed === 'object') {
        return {
          utm_source: parsed.utm_source || null,
          utm_medium: parsed.utm_medium || null,
          utm_campaign: parsed.utm_campaign || null,
          utm_term: parsed.utm_term || null,
          utm_content: parsed.utm_content || null,
        };
      }
    }
  } catch {
    // Return empty fallback
  }

  return defaultEmpty;
}

// Auto-run on module load in browser context
if (typeof window !== 'undefined') {
  initAttribution();
}
